/**
 * Page Editor API requires `fractionalIndex` on StowedElement for add/update (and related routes).
 * When the server omits it (legacy checkpoint) or for brand-new client objects, derive a stable
 * unique string from `objectId` so validation passes and values stay stable across retries.
 */
export function fractionalIndexForStowedObject(objectId: string, explicit?: string | null): string {
  const trimmed = typeof explicit === 'string' ? explicit.trim() : ''

  if (trimmed !== '') return trimmed

  const compact = objectId.replace(/-/g, '')

  return `u${compact}`
}

const FI_DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

function fiOrd(c: string): number {
  return FI_DIGITS.indexOf(c)
}

function fiChr(i: number): string {
  return FI_DIGITS[i] ?? ''
}

/**
 * Returns a fractional index key strictly between a and b.
 * Pass b as empty string "" to mean "end of list" (greater than everything).
 * Ported from the Go backend FractionalIndexMidpoint.
 */
export function fractionalIndexMidpoint(a: string, b: string): string {
  if (b !== '') {
    let n = 0

    while (n < a.length && n < b.length && a[n] === b[n]) n++

    if (n > 0) {
      return b.slice(0, n) + fractionalIndexMidpoint(a.slice(n), b.slice(n))
    }
  }

  const da = a.length > 0 ? fiOrd(a[0]!) : 0
  const db = b !== '' && b.length > 0 ? fiOrd(b[0]!) : FI_DIGITS.length

  if (db - da > 1) return fiChr(Math.floor((da + db) / 2))

  const aTail = a.length > 1 ? a.slice(1) : ''

  if (b !== '' && b.length > 1) return b[0]! + fractionalIndexMidpoint(aTail, '')

  return fiChr(da) + fractionalIndexMidpoint(aTail, '')
}

/**
 * Given a new element ordering (afterOrder) and a function to look up each element's current
 * fractionalIndex, returns a map of element IDs → new FI values for only those elements whose
 * FI needs to change to reflect the new ordering.
 *
 * Unmoved elements (whose current FI is already consistent with the new order) are not included.
 */
export function computeReorderedFractionalIndexes(
  afterOrder: string[],
  getFI: (id: string) => string | undefined
): Map<string, string> {
  const result = new Map<string, string>()
  let prevFI = ''

  for (let i = 0; i < afterOrder.length; i++) {
    const id = afterOrder[i]!
    const currentFI = getFI(id) ?? ''

    if (currentFI > prevFI) {
      prevFI = currentFI
      continue
    }

    // Find next unprocessed element whose original FI is > prevFI (use as upper bound)
    let nextFI = ''

    for (let j = i + 1; j < afterOrder.length; j++) {
      const nextCurrentFI = getFI(afterOrder[j]!) ?? ''

      if (nextCurrentFI > prevFI) {
        nextFI = nextCurrentFI
        break
      }
    }

    const newFI = fractionalIndexMidpoint(prevFI, nextFI)

    result.set(id, newFI)
    prevFI = newFI
  }

  return result
}
